import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiGetSessionStatus, apiLogout, apiRenewSessionActivity } from "../../lib/api";
import { isLoggedIn } from "../../hooks/usePortalAuth";
import { broadcastPortalLogout, performLogout } from "../../lib/auth";
import { DESIGN_PREVIEW_MODE } from "../../lib/previewMode";

export const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
export const INACTIVITY_WARNING_MS = 28 * 60 * 1000;
export const ACTIVITY_RENEWAL_THROTTLE_MS = 60 * 1000;

const ACTIVITY_KEY = "portal_last_meaningful_activity";
const LOGOUT_KEY = "portal_session_logout";

export default function InactivitySessionGuard() {
  const location = useLocation();
  const [warning, setWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  const lastRenewal = useRef(0);
  const renewalPending = useRef(false);
  const pendingActivityAt = useRef(0);
  const pendingActivityNeedsAcceptance = useRef(false);
  const telehealthExemptUntil = useRef(0);
  const revocationStarted = useRef(false);
  const scheduleGeneration = useRef(0);
  const warningTimer = useRef<number | undefined>(undefined);
  const logoutTimer = useRef<number | undefined>(undefined);
  const trailingRenewalTimer = useRef<number | undefined>(undefined);

  const revokeFrontendSession = useCallback(() => {
    if (revocationStarted.current) return;
    revocationStarted.current = true;
    performLogout();
    broadcastPortalLogout();
    window.location.assign("/clientportal");
  }, []);

  const endSession = useCallback(async (generation: number) => {
    const status = await apiGetSessionStatus();
    if ([401, 403].includes(status.status)) {
      revokeFrontendSession();
      return;
    }
    if (generation !== scheduleGeneration.current) return;
    const exemptUntil = status.data?.session?.telehealth_exempt_until
      ? new Date(status.data.session.telehealth_exempt_until).getTime()
      : 0;
    if (status.data && exemptUntil > Date.now()) {
      telehealthExemptUntil.current = exemptUntil;
      setWarning(false);
      window.clearTimeout(logoutTimer.current);
      logoutTimer.current = window.setTimeout(() => { void endSession(generation); }, Math.max(0, exemptUntil - Date.now()));
      return;
    }
    await apiLogout();
    revokeFrontendSession();
  }, [revokeFrontendSession]);

  const schedule = useCallback((generation = scheduleGeneration.current) => {
    window.clearTimeout(warningTimer.current);
    window.clearTimeout(logoutTimer.current);
    if (!isLoggedIn() || DESIGN_PREVIEW_MODE) return;
    const elapsed = Date.now() - lastActivity.current;
    warningTimer.current = window.setTimeout(() => {
      void apiGetSessionStatus().then((status) => {
        if ([401, 403].includes(status.status)) {
          revokeFrontendSession();
          return;
        }
        if (generation !== scheduleGeneration.current) return;
        const until = status.data?.session?.telehealth_exempt_until
          ? new Date(status.data.session.telehealth_exempt_until).getTime()
          : 0;
        telehealthExemptUntil.current = until;
        if (status.data && until <= Date.now()) setWarning(true);
      });
    }, Math.max(0, INACTIVITY_WARNING_MS - elapsed));
    logoutTimer.current = window.setTimeout(() => { void endSession(generation); }, Math.max(0, INACTIVITY_LIMIT_MS - elapsed));
  }, [endSession, revokeFrontendSession]);

  const applyMeaningfulActivity = useCallback((now: number) => {
    lastActivity.current = now;
    scheduleGeneration.current += 1;
    setWarning(false);
    localStorage.setItem(ACTIVITY_KEY, String(now));
    schedule(scheduleGeneration.current);
  }, [schedule]);

  const renewPendingActivity = useCallback(() => {
    if (renewalPending.current || !pendingActivityAt.current || revocationStarted.current) return;
    window.clearTimeout(trailingRenewalTimer.current);
    trailingRenewalTimer.current = undefined;
    const activityBeingRenewed = pendingActivityAt.current;
    const acceptOnSuccess = pendingActivityNeedsAcceptance.current;
    pendingActivityAt.current = 0;
    pendingActivityNeedsAcceptance.current = false;
    renewalPending.current = true;
    void apiRenewSessionActivity().then((response) => {
      renewalPending.current = false;
      if ([401, 403].includes(response.status)) {
        revokeFrontendSession();
        return;
      }
      if (!response.data?.session?.last_activity_at) {
        pendingActivityAt.current = Math.max(pendingActivityAt.current, activityBeingRenewed);
        pendingActivityNeedsAcceptance.current ||= acceptOnSuccess;
        trailingRenewalTimer.current = window.setTimeout(renewPendingActivity, ACTIVITY_RENEWAL_THROTTLE_MS);
        return;
      }
      lastRenewal.current = new Date(response.data.session.last_activity_at).getTime();
      const until = response.data.session.telehealth_exempt_until;
      telehealthExemptUntil.current = until ? new Date(until).getTime() : 0;
      if (acceptOnSuccess) applyMeaningfulActivity(activityBeingRenewed);
      if (pendingActivityAt.current) {
        trailingRenewalTimer.current = window.setTimeout(renewPendingActivity, ACTIVITY_RENEWAL_THROTTLE_MS);
      }
    });
  }, [applyMeaningfulActivity, revokeFrontendSession]);

  const scheduleTrailingRenewal = useCallback(() => {
    if (renewalPending.current || trailingRenewalTimer.current !== undefined || !pendingActivityAt.current) return;
    const delay = Math.max(0, lastRenewal.current + ACTIVITY_RENEWAL_THROTTLE_MS - Date.now());
    trailingRenewalTimer.current = window.setTimeout(renewPendingActivity, delay);
  }, [renewPendingActivity]);

  const meaningfulActivity = useCallback(() => {
    if (!isLoggedIn() || DESIGN_PREVIEW_MODE) return;
    const now = Date.now();
    pendingActivityAt.current = now;
    if (renewalPending.current) {
      if (lastRenewal.current && now - lastRenewal.current < ACTIVITY_RENEWAL_THROTTLE_MS) applyMeaningfulActivity(now);
      else pendingActivityNeedsAcceptance.current = true;
      return;
    }
    if (!lastRenewal.current || now - lastRenewal.current >= ACTIVITY_RENEWAL_THROTTLE_MS) {
      pendingActivityNeedsAcceptance.current = true;
      renewPendingActivity();
      return;
    }
    applyMeaningfulActivity(now);
    scheduleTrailingRenewal();
  }, [applyMeaningfulActivity, renewPendingActivity, scheduleTrailingRenewal]);

  useEffect(() => {
    const stored = Number(localStorage.getItem(ACTIVITY_KEY));
    lastActivity.current = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    schedule();
    const events = ["pointerdown", "pointermove", "keydown", "click", "touchstart", "input", "change"] as const;
    events.forEach((event) => window.addEventListener(event, meaningfulActivity, { passive: true }));
    const onStorage = (event: StorageEvent) => {
      if (event.key === LOGOUT_KEY) {
        performLogout();
        window.location.assign("/clientportal");
      }
      if (event.key === ACTIVITY_KEY && event.newValue) {
        lastActivity.current = Number(event.newValue) || Date.now();
        scheduleGeneration.current += 1;
        setWarning(false);
        schedule(scheduleGeneration.current);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      events.forEach((event) => window.removeEventListener(event, meaningfulActivity));
      window.removeEventListener("storage", onStorage);
      window.clearTimeout(warningTimer.current);
      window.clearTimeout(logoutTimer.current);
      window.clearTimeout(trailingRenewalTimer.current);
    };
  }, [meaningfulActivity, schedule]);

  useEffect(() => {
    meaningfulActivity();
  }, [location.pathname, location.search, meaningfulActivity]);

  if (!warning || !isLoggedIn() || DESIGN_PREVIEW_MODE) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-dark/35 px-4" role="alertdialog" aria-modal="true" aria-labelledby="inactivity-warning-title">
      <div className="w-full max-w-md rounded-2xl border border-brand-border bg-white p-6 shadow-xl">
        <h2 id="inactivity-warning-title" className="font-lora text-xl font-semibold text-brand-dark">You’ll be signed out soon</h2>
        <p className="mt-2 font-manrope text-sm text-brand-dark/70">For your security, your portal session will end after 30 minutes without activity. Stay signed in to protect any unsaved work.</p>
        <button type="button" onClick={meaningfulActivity} className="mt-5 rounded-lg bg-brand-blue px-4 py-2 font-manrope text-sm font-semibold text-white hover:bg-brand-blue/90 focus:outline-none focus:ring-2 focus:ring-brand-blue/40">Stay signed in</button>
      </div>
    </div>
  );
}
